import React from "react";
import { FieldErrors, SubmitHandler } from "react-hook-form";
import {
  UseFormRegister,
  UseFormSetError,
} from "react-hook-form/dist/types/form";
import { UseMutationResult } from "react-query";
import { twMerge } from "tailwind-merge";
import { CampaignTypes } from "../../../models/Campaign";
import { Modal } from "../../Modal/Modal";
import StepOne from "../CampaignSteps/StepOne";
import StepThree from "../CampaignSteps/StepThree";
import StepTwo from "../CampaignSteps/StepTwo";
import { useTranslation } from "react-i18next"

type Props = {
  isAddCampaign: boolean;
  setIsAddCampaign: React.Dispatch<React.SetStateAction<boolean>>;
  campaign: CampaignTypes;
  setCampaign: React.Dispatch<React.SetStateAction<CampaignTypes>>;
  register: UseFormRegister<CampaignTypes>;
  errors: FieldErrors<CampaignTypes>;
  setError: UseFormSetError<CampaignTypes>;
  addCampaignMutation: UseMutationResult<any, unknown, CampaignTypes, unknown>;
  isValid: boolean;
  setCampaignStep: React.Dispatch<React.SetStateAction<number>>;
  campaignStep: number;
  onSubmit: SubmitHandler<CampaignTypes>;
  handleSubmit: (
    onSubmit: SubmitHandler<CampaignTypes>
  ) => (e: React.BaseSyntheticEvent) => void;
  editMode?: boolean;
  setEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
  setCampaignId?: React.Dispatch<React.SetStateAction<string>>;
  categories: string[];
  countries: string[];
  brands: string[];
  products: string[];
  reset: () => void;
};

const INITIAL_PAYLOAD: CampaignTypes = {
  partnerId: "",
  isPermanent: false,
  title: "",
  startDate: "",
  endDate: "",
  category: "",
  offers: [
    {
      title: "",
      cta: "",
      description: "",
      image: "",
      url: "",
    },
  ],
  parameters: {},
  active: "false",
  state: "draft",
};

const CampaignModal = ({

  isAddCampaign,
  setIsAddCampaign,
  campaign,
  setCampaign,
  register,
  errors,
  addCampaignMutation,
  onSubmit,
  setError,
  isValid,
  setCampaignStep,
  campaignStep,
  handleSubmit,
  editMode,
  setEditMode,
  setCampaignId,
  countries,
  brands,
  products,
  categories,
  reset,
}: Props) => {
  const campaignStepHandler = () => {
    switch (campaignStep) {
      case 1:
        return (
          <StepOne
            campaign={campaign}
            setCampaign={setCampaign}
            register={register}
            errors={errors}
            setStep={setCampaignStep}
            isValid={isValid}
          />
        );
      case 2:
        return (
          <StepTwo
            campaign={campaign}
            setCampaign={setCampaign}
            setError={setError}
            register={register}
            errors={errors}
            setStep={setCampaignStep}
            isValid={isValid}
          />
        );
      case 3:
        return (
          <StepThree
            campaign={campaign}
            setCampaign={setCampaign}
            register={register}
            errors={errors}
            countries={countries}
            brands={brands}
            products={products}
            categories={categories}
            onSubmit={onSubmit}
            handleSubmit={handleSubmit}
            setStep={setCampaignStep}
            isValid={isValid}
            addCampaignMutation={addCampaignMutation}
          />
        );

      default:
        return (
          <StepOne
            campaign={campaign}
            setCampaign={setCampaign}
            register={register}
            errors={errors}
            setStep={setCampaignStep}
            isValid={isValid}
          />
        );
    }
  };
  const { t } = useTranslation();
  return (
    <Modal
      visible={isAddCampaign}
      onClose={() => {
        setCampaignId && setCampaignId("");
        setEditMode && setEditMode(false);
        setCampaignStep(1);
        setCampaign(INITIAL_PAYLOAD);
        reset();
        setIsAddCampaign(false);
      }}
      backdropClose={false}
      noPadding={true}
    >
      <div className="w-screen md:w-[1050px] sm:w-[400px]">
        {/* Tabs are now full width */}
        <div className="flex border-b border-gray-200">
          {/* ... existing tab code is fine ... */}
          <div
            className={twMerge(
              "flex-1 px-4 py-4 flex flex-col justify-center border-b-2 transition-colors duration-200 cursor-pointer", // Added cursor-pointer
              campaignStep === 1
                ? "bg-white border-indigo-500 text-indigo-700"
                : "bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100"
            )}
            onClick={() => setCampaignStep(1)} // Allow clicking back
          >
            {campaignStep === 1 && (
              <h1 className="text-base font-bold mb-1">
                {editMode ? t("campaign.edit") : t("campaign.create")}
              </h1>
            )}
            <h1 className={twMerge("text-sm", campaignStep === 1 ? "font-medium" : "font-normal")}>{t("campaign.step1")}</h1>
          </div>
          <div
            className={twMerge(
              "flex-1 px-4 py-4 flex flex-col justify-center border-b-2 transition-colors duration-200",
              // Step 2 isn't clickable unless step 1 is valid, but for now let's just style it
              campaignStep === 2
                ? "bg-white border-indigo-500 text-indigo-700"
                : "bg-gray-50 border-transparent text-gray-500"
            )}
          >
            {campaignStep === 2 && (
              <h1 className="text-base font-bold mb-1">
                {editMode ? t("campaign.edit") : t("campaign.create")}
              </h1>
            )}
            <h1 className={twMerge("text-sm", campaignStep === 2 ? "font-medium" : "font-normal")}>{t("campaign.step2")}</h1>
          </div>

          <div
            className={twMerge(
              "flex-1 px-4 py-4 flex flex-col justify-center border-b-2 transition-colors duration-200",
              campaignStep === 3
                ? "bg-white border-indigo-500 text-indigo-700"
                : "bg-gray-50 border-transparent text-gray-500"
            )}
          >
            {campaignStep === 3 && (
              <h1 className="text-base font-bold mb-1">
                {editMode ? t("campaign.edit") : t("campaign.create")}
              </h1>
            )}
            <h1 className={twMerge("text-sm", campaignStep === 3 ? "font-medium" : "font-normal")}>{t("campaign.step3")}</h1>
          </div>
        </div>

        {/* Content with padding */}
        <div className="p-8">
          <form onSubmit={handleSubmit(onSubmit)}>{campaignStepHandler()}</form>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(CampaignModal);
